import {
  ScrollObserver
} from "./chunk-PROZAJEM.js";

// src/layouts/card-stack/card-stack.tsx
import CardHero from "@a24/ui/CardHero";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useCallback, useRef } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
gsap.registerPlugin(ScrollTrigger, useGSAP);
var CardStack = ({
  cards,
  className = "",
  id,
  labels = {
    scrollToExplore: "Scroll to explore shows"
  }
}) => {
  if (!cards) return null;
  const wrapperRef = useRef(null);
  const gsapRefs = useRef([]);
  const shadowRef = useRef(null);
  function computeDims(index) {
    switch (index) {
      case 0:
        return "top-[calc(100vh-31.125rem)] h-[31.125rem] lg:top-[calc(100vh-50.75rem)] lg:h-[50.75rem]";
      case 1:
        return "top-[calc(100vh-30.625rem)] h-[30.625rem] lg:top-[calc(100vh-47.75rem)] lg:h-[47.75rem]";
      case 2:
        return "top-[calc(100vh-30.125rem)] h-[30.125rem] lg:top-[calc(100vh-44.75rem)] lg:h-[44.75rem]";
      case 3:
        return "top-[calc(100vh-29.625rem)] h-[29.625rem] lg:top-[calc(100vh-41.75rem)] lg:h-[41.75rem]";
      case 4:
        return "top-[calc(100vh-29.125rem)] h-[29.125rem] lg:top-[calc(100vh-38.75rem)] lg:h-[38.75rem]";
      default:
        return "top-[calc(100vh-31.125rem)] h-[31.125rem] lg:top-[calc(100vh-50.75rem)] lg:h-[50.75rem]";
    }
  }
  const positions = cards?.map((card, index) => {
    return computeDims(index);
  }) || [];
  const setHiddenState = useCallback((index, isHidden) => {
    const eltToSet = gsapRefs.current[index - 1];
    if (eltToSet) {
      eltToSet.dataset.hidden = isHidden;
    }
  }, []);
  const createTriggers = useCallback(
    (reduceMotion) => {
      gsapRefs.current.forEach((ref, index) => {
        if (ref) {
          const topOffset = Number.parseInt(
            getComputedStyle(ref)?.top?.replace("px", "") || "0",
            10
          );
          ScrollTrigger.create({
            trigger: ref,
            start: `top top+=${topOffset}`,
            onEnter: () => {
              setHiddenState(index, "hidden");
            },
            onLeaveBack: (self) => {
              setHiddenState(index, "");
            }
          });
          if (index === 0 && !reduceMotion) {
            const topVal = ref.getBoundingClientRect().top + window.scrollY;
            const homeVideo = document.querySelector("#home-video");
            ScrollTrigger.create({
              trigger: ref,
              start: `top top+=${topVal}`,
              end: `top top+=${topOffset}`,
              scrub: true,
              onUpdate: (self) => {
                gsap.set(shadowRef.current, {
                  opacity: Math.min(1, 2 * self.progress)
                });
                if (homeVideo) {
                  gsap.set(homeVideo, {
                    opacity: 1 - self.progress
                  });
                }
              }
            });
          }
        }
      });
    },
    [setHiddenState]
  );
  const clearTriggers = useCallback(() => {
    for (let i = 1; i < gsapRefs.current.length + 1; i++) {
      setHiddenState(i, "");
    }
  }, [setHiddenState]);
  function handleEnter(e) {
    if (!wrapperRef.current) {
      return;
    }
    const elt = e.currentTarget;
    if (elt.dataset.hidden === "hidden") {
      const pt = Number.parseInt(
        getComputedStyle(wrapperRef.current)?.paddingTop?.replace("px", "") || "",
        10
      );
      const top = (wrapperRef.current?.offsetTop || 0) + pt + Math.max(0, gsapRefs.current.indexOf(elt)) * elt.offsetHeight;
      window.scrollTo({
        top,
        behavior: "smooth"
      });
    }
  }
  useGSAP(
    () => {
      if (cards) {
        gsapRefs.current = gsapRefs.current.slice(0, cards.length);
        const mm = gsap.matchMedia();
        mm.add(
          {
            isDesktop: "(min-width: 1024px)",
            isMobile: "(max-width: 1023px)",
            reduceMotion: "(prefers-reduced-motion: reduce)"
          },
          (context) => {
            const { reduceMotion, isDesktop } = context.conditions;
            clearTriggers();
            createTriggers(reduceMotion);
            if (isDesktop) {
              window.addEventListener("resize", gsap.matchMediaRefresh);
            }
            return () => {
              window.removeEventListener("resize", gsap.matchMediaRefresh);
            };
          }
        );
      }
    },
    { dependencies: [cards], scope: wrapperRef }
  );
  return /* @__PURE__ */ jsxs("div", { className: `${className} relative`, id, ref: wrapperRef, children: [
    /* @__PURE__ */ jsx("div", { className: "pointer-events-none absolute top-0 w-full h-[calc(100%-31.125rem)] lg:h-[calc(100%-50.75rem)]", children: /* @__PURE__ */ jsx(ScrollObserver, { useSelf: true, theme: "theme-dark-transparent" }) }),
    /* @__PURE__ */ jsx("div", { className: "pointer-events-none absolute bottom-0 w-full h-[31.125rem] lg:h-[50.75rem]", children: /* @__PURE__ */ jsx(ScrollObserver, { useSelf: true, theme: "theme-light" }) }),
    cards?.slice(0, 5).map((card, index) => {
      return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
        /* @__PURE__ */ jsxs(
          "div",
          {
            ref: (el) => {
              gsapRefs.current[index] = el;
            },
            className: [
              `sticky w-[100%] ${positions[index]} data-[hidden="hidden"]:cursor-pointer`
            ].join(" "),
            onClick: handleEnter,
            onFocus: handleEnter,
            children: [
              index === 0 && /* @__PURE__ */ jsx(
                "div",
                {
                  ref: shadowRef,
                  className: [
                    "gradient-bottom-top",
                    "absolute bottom-0 left-0 full-viewport-width",
                    // 'h-[calc(80%+19.22rem)]',
                    "lg:h-[calc(80%+28.6875rem)]",
                    "pointer-events-none opacity-0"
                  ].join(" ")
                }
              ),
              /* @__PURE__ */ jsx(
                CardHero,
                {
                  ...card,
                  className: [
                    "h-[100%] relative transition-home-hero-opacity delay-200",
                    "opacity-0 translate-y-[15.56rem] not-[body.home-ready_&]:duration-0",
                    "[body.home-scrolled_&]:duration-0 [body.home-ready_&]:opacity-100",
                    "[body.home-ready_&]:translate-y-0"
                  ].join(" ")
                }
              )
            ]
          },
          card.title
        )
      );
    })
  ] });
};
var card_stack_default = CardStack;

export {
  card_stack_default
};
